// Group memberships (MEMBERSHIPS_PLAN.md D42), declared conditions (D43),
// introductory types (D44) and types that need a child's membership (D45):
// the pure, unit-tested half. Server-built copy (whoItCovers, slot lines,
// newToClubLine, renewsAsLine, needsChildLine, groupLine) is shown as sent;
// what lives here is what the editor and the apply flow need BEFORE a save:
// live previews that mirror MembershipGroupRules, place picking by age, and
// the request shapes. Never "members only" (D31).

import {dateOnlyToLocal, toDateOnly} from '~/utils/dateOnly';
import {withNoun} from '~/utils/membershipWords';

export const COVERS = Object.freeze({onePerson: 'OnePerson', group: 'Group'});

export const isGroupType = (type) => type?.covers === COVERS.group;

/** The API's limits (MembershipTypeValidator). */
export const SLOT_MAX_COUNT = 20;
export const MAX_CONDITIONS = 10;
export const CONDITION_MAX_LENGTH = 300;

const num = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? null : Number(v));

// ── Places (D42) ───────────────────────────────────────────────────────────

export const slotHasAgeBand = (slot) => num(slot?.minAge) != null || num(slot?.maxAge) != null;

/** Mirrors MembershipGroupRules.SlotAges: "26 and over", "Under 26", "Ages 8 to 11", "Any age". */
export function slotAges(slot) {
  const min = num(slot?.minAge);
  const max = num(slot?.maxAge);
  if (min != null && max != null) return `Ages ${min} to ${max}`;
  if (min != null) return `${min} and over`;
  if (max != null) return `Under ${max + 1}`;
  return 'Any age';
}

/** Mirrors MembershipGroupRules.SlotLine: "1 to 2 Adults (26 and over)", "2 Parents", "Up to 6 Children (under 26)". */
export function slotLine(slot) {
  const minCount = num(slot?.minCount) ?? 0;
  const maxCount = num(slot?.maxCount) ?? 1;
  const count = minCount === maxCount ? `${maxCount}` : minCount === 0 ? `Up to ${maxCount}` : `${minCount} to ${maxCount}`;
  const ages = slotHasAgeBand(slot) ? ` (${slotAges(slot).toLowerCase().replace('ages ', 'aged ')})` : '';
  return `${count} ${String(slot?.label ?? '').trim()}${ages}`;
}

/** Mirrors MembershipGroupRules.WhoItCovers for the editor's live preview. */
export function whoItCoversText(slots) {
  const lines = [...(slots ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map(slotLine);
  if (lines.length === 0) return 'For a group';
  const joined = lines.length === 1 ? lines[0] : `${lines.slice(0, -1).join(', ')} and ${lines[lines.length - 1].replace('Up to', 'up to')}`;
  return `For ${joined}`;
}

export const maxPeople = (slots) => (slots ?? []).reduce((sum, s) => sum + (num(s.maxCount) ?? 0), 0);
export const minPeople = (slots) => (slots ?? []).reduce((sum, s) => sum + (num(s.minCount) ?? 0), 0);

/** A new place in the editor. The first one is the adults' place, the next the children's. */
export function blankSlot(existing = []) {
  const first = existing.length === 0;
  return {id: null, label: first ? 'Adults' : 'Children', minCount: first ? 1 : 0, maxCount: first ? 2 : 4, minAge: null, maxAge: null, nextTypeOnAgeOutId: null, sortOrder: existing.length};
}

/** The editor's slot rows → MembershipTypeSlotRequest[]. */
export function slotsForApi(slots) {
  return (slots ?? []).map((s, i) => ({
    id: s.id ?? null,
    label: String(s.label ?? '').trim(),
    minCount: Math.max(0, num(s.minCount) ?? 0),
    maxCount: Math.max(1, num(s.maxCount) ?? 1),
    minAge: num(s.minAge),
    maxAge: num(s.maxAge),
    nextTypeOnAgeOutId: num(s.maxAge) != null ? (s.nextTypeOnAgeOutId ?? null) : null,
    sortOrder: i,
  }));
}

/** The first problem with the places, in the editor's words, or null. */
export function slotsProblem(slots) {
  const list = slots ?? [];
  if (list.length === 0) return 'Add at least one place, such as Adults';
  for (const s of list) {
    const label = String(s.label ?? '').trim();
    if (!label) return 'Give every place a name, such as Adults or Children';
    const min = num(s.minCount) ?? 0;
    const max = num(s.maxCount);
    if (max == null || max < 1) return `The ${label} place needs room for at least 1 person`;
    if (max > SLOT_MAX_COUNT) return `The ${label} place can hold up to ${SLOT_MAX_COUNT} people`;
    if (min > max) return `The ${label} place can't need more people than it holds`;
    if (num(s.minAge) != null && num(s.maxAge) != null && num(s.minAge) > num(s.maxAge)) return `The youngest age for ${label} can't be above the oldest`;
  }
  const labels = list.map(s => String(s.label ?? '').trim().toLowerCase());
  if (new Set(labels).size !== labels.length) return 'Two places have the same name';
  if (minPeople(list) < 1) return 'At least one place needs 1 person or more';
  return null;
}

// ── Ages and placing people ────────────────────────────────────────────────

/** Whole years on a day (the API works ages out on the type's own date; this is the preview). */
export function ageOn(dateOfBirth, onDate = new Date()) {
  // A date of birth is a calendar day: read without a timezone shift (utils/dateOnly).
  const dob = dateOnlyToLocal(dateOfBirth);
  if (!dob) return null;
  const on = new Date(onDate);
  let age = on.getFullYear() - dob.getFullYear();
  const m = on.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && on.getDate() < dob.getDate())) age--;
  return age;
}

export const ageFits = (age, slot) => !slotHasAgeBand(slot)
    || (age != null && (num(slot.minAge) == null || age >= num(slot.minAge)) && (num(slot.maxAge) == null || age <= num(slot.maxAge)));

/**
 * Mirrors MembershipGroupRules.AssignSeats: asked-for places first, then the
 * first place (in order) whose ages fit and that has room. seats: [{name, age,
 * slotId, anyAge}] → {slotIds, problem}. With enforceMinimums the places' minimums
 * must be met too. The server has the final say; this is the live preview.
 */
export function assignPlaces(slots, seats, {enforceMinimums = false, taken = {}} = {}) {
  const live = [...(slots ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const slotIds = seats.map(() => null);
  if (live.length === 0) return {slotIds, problem: 'This membership has no places set up yet. Ask the club.'};
  // taken: people already on the group, by place (adding someone later).
  const counts = Object.fromEntries(live.map(s => [s.id, taken?.[s.id] ?? 0]));
  const covers = () => whoItCoversText(live).replace('For ', 'for ');
  // A row with no name yet reads "this person" mid-sentence and "This person" to start one.
  const who = (seat, {start = false} = {}) => {
    const name = String(seat?.name ?? '').trim();
    if (name && name !== 'This person') return name;
    return start ? 'This person' : 'this person';
  };
  for (let i = 0; i < seats.length; i++) {
    const seat = seats[i];
    if (!seat.slotId) continue;
    const slot = live.find(s => s.id === seat.slotId);
    if (!slot) return {slotIds, problem: `That place isn't one of this membership's (${who(seat)}).`};
    if (!seat.anyAge && !ageFits(seat.age, slot)) {
      return {slotIds, problem: seat.age == null
          ? `Add ${who(seat)}'s date of birth: the ${slot.label} place is for ${slotAges(slot).toLowerCase()}.`
          : `${who(seat, {start: true})} can't be in the ${slot.label} place: it is for ${slotAges(slot).toLowerCase()}.`};
    }
    if (counts[slot.id] >= slot.maxCount) return {slotIds, problem: `The ${slot.label} place is for up to ${slot.maxCount} ${slot.maxCount === 1 ? 'person' : 'people'}.`};
    slotIds[i] = slot.id;
    counts[slot.id]++;
  }
  for (let i = 0; i < seats.length; i++) {
    const seat = seats[i];
    if (seat.slotId) continue;
    // anyAge: someone whose age only the server knows (a roster player picked by an admin).
    const slot = live.find(s => (seat.anyAge || ageFits(seat.age, s)) && counts[s.id] < s.maxCount);
    if (!slot) {
      if (seat.age == null && live.some(slotHasAgeBand)) return {slotIds, problem: `Add ${who(seat)}'s date of birth so we can see which place they fit.`};
      const fitsAge = live.some(s => ageFits(seat.age, s));
      return {slotIds, problem: fitsAge ? `There's no room left for ${who(seat)}: this membership is ${covers()}.` : `${who(seat, {start: true})} doesn't fit any place on this membership (${covers()}).`};
    }
    slotIds[i] = slot.id;
    counts[slot.id]++;
  }
  if (enforceMinimums) {
    const short = live.find(s => counts[s.id] < (s.minCount ?? 0));
    if (short) {
      const missing = short.minCount - counts[short.id];
      return {slotIds, problem: `This membership needs ${short.minCount === 1 ? '1 person' : `${short.minCount} people`} in the ${short.label} place. Add ${missing === 1 ? 'one more' : `${missing} more`}.`};
    }
  }
  return {slotIds, problem: null};
}

// ── The people on an application or a group (D42) ──────────────────────────

/** A person row in the apply flow or the admin add dialog. */
export function blankPerson(key) {
  return {key: key ?? `p${Math.random().toString(36).slice(2, 9)}`, isMe: false, playerId: null, firstName: '', lastName: '', dateOfBirth: null, email: '', slotId: null};
}

/** Mirrors MembershipGroupPeople: the buyer answers for themselves and anyone without an email; the rest may answer later. */
export const buyerAnswersFor = (person) => !!person?.isMe || !String(person?.email ?? '').trim();

/** "Kim" / "You" for a person row. */
export const personName = (person, {me = 'You'} = {}) => person?.isMe ? me : (String(person?.firstName ?? '').trim() || 'This person');

const looksLikeEmail = (text) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(text ?? '').trim());

/**
 * Problems on the People step, keyed by the row's key then the field, before
 * anything is sent: names, a date of birth when any place has ages, a
 * well-formed email when one is given, and the same person twice.
 */
export function peopleProblems(people, {slots = [], myEmail = null} = {}) {
  const problems = {};
  const needsDob = (slots ?? []).some(slotHasAgeBand);
  const seen = new Set();
  for (const p of people ?? []) {
    if (p.isMe || p.playerId) continue;
    const row = {};
    if (!String(p.firstName ?? '').trim()) row.firstName = 'Their first name';
    if (!String(p.lastName ?? '').trim()) row.lastName = 'Their last name';
    if (needsDob && !p.dateOfBirth) row.dateOfBirth = 'Their date of birth';
    const email = String(p.email ?? '').trim().toLowerCase();
    if (email && !looksLikeEmail(email)) row.email = 'A full email address';
    else if (email && myEmail && email === String(myEmail).trim().toLowerCase()) row.email = 'That\'s your own email. Tick "This includes me" instead';
    const who = `${String(p.firstName ?? '').trim().toLowerCase()} ${String(p.lastName ?? '').trim().toLowerCase()} ${toDateOnly(p.dateOfBirth) ?? ''}`;
    if (!row.firstName && !row.lastName && seen.has(who)) row.firstName = 'This person is on the list twice';
    seen.add(who);
    if (Object.keys(row).length) problems[p.key] = row;
  }
  return problems;
}

/**
 * The People step → MembershipGroupPersonRequest[]. answersByKey / healthByKey
 * hold what the buyer gave for each person they answer for; consent is the
 * buyer's one health tick (D42: it covers everyone they answer for).
 */
export function peopleForApi(people, {answersByKey = {}, healthByKey = {}, consent = false} = {}) {
  return (people ?? []).map((p) => {
    const answers = answersByKey[p.key] ?? [];
    const health = healthByKey[p.key];
    if (p.isMe) return {isMe: true, slotId: p.slotId ?? null, answers, health: health ? {consent: !!consent, answers: health} : null};
    if (p.playerId) return {isMe: false, playerId: p.playerId, dateOfBirth: toDateOnly(p.dateOfBirth), slotId: p.slotId ?? null, answers, health: health ? {consent: !!consent, answers: health} : null};
    return {
      isMe: false,
      firstName: String(p.firstName ?? '').trim(),
      lastName: String(p.lastName ?? '').trim(),
      dateOfBirth: toDateOnly(p.dateOfBirth),
      email: String(p.email ?? '').trim() || null,
      slotId: p.slotId ?? null,
      answers,
      health: health ? {consent: !!consent, answers: health} : null,
    };
  });
}

/** "Children · 9 · Pays" under a person on a group (MembershipGroupPersonResponse). */
export function groupPersonSub(person) {
  return [
    person?.slotLabel,
    person?.age != null ? `${person.age}` : null,
    person?.isLead ? 'Pays' : null,
    person?.isBuyer && !person?.isLead ? 'Bought it' : null,
    !person?.hasAccount ? (person?.invited ? 'Invited' : 'No account') : null,
  ].filter(Boolean).join(' · ');
}

/** The people still on a group (not left), lead first then in place order. */
export function currentGroupPeople(group) {
  const order = Object.fromEntries((group?.slots ?? []).map((s, i) => [s.id, s.sortOrder ?? i]));
  return [...(group?.people ?? [])]
      .filter(p => !p.leftGroupAt || p.leavesOn)
      .sort((a, b) => (b.isLead ? 1 : 0) - (a.isLead ? 1 : 0) || (order[a.slotId] ?? 99) - (order[b.slotId] ?? 99));
}

// ── Conditions (D43) ───────────────────────────────────────────────────────

/** The editor's condition rows → MembershipConditionRequest[] (empty rows dropped; an unchanged id keeps the condition). */
export function conditionsForApi(conditions) {
  return (conditions ?? [])
      .map(c => ({id: c.id ?? null, text: String(c.text ?? '').trim()}))
      .filter(c => c.text);
}

/** The suggestions not already on the type (case-insensitive). */
export function unusedConditionSuggestions(suggestions, conditions) {
  const have = new Set((conditions ?? []).map(c => String(c.text ?? '').trim().toLowerCase()));
  return (suggestions ?? []).filter(s => !have.has(String(s).trim().toLowerCase()));
}

/** Every condition ticked? (one tick per condition for the whole group). */
export const allConditionsTicked = (conditions, ticked) => (conditions ?? []).every(c => (ticked ?? []).includes(c.id));

// ── Introductory (D44) and needs a child's membership (D45) ────────────────

/** Types an introductory type may renew into: not itself, same Covers, not a set-dates type, not one that renews back into it. */
export function renewsAsOptions(types, form) {
  return (types ?? []).filter(t => t.id !== form?.id && !t.archivedAt
      && (t.covers ?? COVERS.onePerson) === (form?.covers ?? COVERS.onePerson)
      && t.length !== 'SetDates'
      && !(form?.id && t.renewsAsTypeId === form.id));
}

/** Types a parent's membership may hang off: this club's, not ones that themselves need a child. */
export function childTypeOptions(types, form) {
  return (types ?? []).filter(t => t.id !== form?.id && !t.archivedAt && !t.needsChildMembership);
}

/** The editor's preview of the API's NewToClubLine. */
export function newToClubText(newToClubYears) {
  const years = num(newToClubYears);
  if (years != null && years > 0) return `For people new to the club (no membership here in the last ${years === 1 ? 'year' : `${years} years`})`;
  return 'For people new to the club';
}

/** Application rows: what a D45 link waits on (linkStatus from the API). */
export function linkStatusLine(application) {
  const child = application?.linkedChildName || "the child";
  switch (application?.linkStatus) {
    case 'waiting': return `Waiting for ${child}'s family to confirm`;
    case 'confirmed': return `Linked to ${child}`;
    case 'declined': return `${child}'s family said no`;
    case 'expired': return `${child}'s family didn't answer in time`;
    default: return null;
  }
}

/**
 * Mirrors the API's quick-add matcher (MembershipTypeService.ApplyQuickAddShape,
 * X19): a suggestion key ("senior", "junior") is matched to this club's own
 * types by the suggestion's name, the exact name first, else names starting
 * with it, so "Senior" finds Senior Adult and "Junior" finds Junior U18 and
 * Junior U10. Renews as: same covers, not set dates, exactly one match. Children:
 * every match that doesn't itself need a child.
 */
const keyLabel = (key, allSuggestions) => {
  const named = (allSuggestions ?? []).find(s => s.key === key)?.name;
  return String(named || key || '').trim();
};
const sameName = (t, label) => String(t?.name ?? '').trim().toLowerCase() === label.toLowerCase();
const startsWithName = (t, label) => String(t?.name ?? '').trim().toLowerCase().startsWith(label.toLowerCase());

/** {typeId, line}: the club's type an introductory type renews into, or the words for why none was picked. */
export function matchRenewsAs(key, clubTypes = [], {covers = COVERS.onePerson, allSuggestions = []} = {}) {
  const label = keyLabel(key, allSuggestions);
  if (!label) return {typeId: null, line: null};
  const fits = (clubTypes ?? []).filter(t => !t.archivedAt && (t.covers ?? COVERS.onePerson) === covers && t.length !== 'SetDates');
  const exact = fits.filter(t => sameName(t, label));
  const pick = exact.length === 1 ? exact : fits.filter(t => startsWithName(t, label));
  if (pick.length === 1) return {typeId: pick[0].id, line: null};
  if (pick.length > 1) return {typeId: null, line: `It renews as itself for now: more than one type could be ${label} (${pick.map(t => t.name).join(', ')}). Choose one under Renews as.`};
  return {typeId: null, line: `It renews as itself for now: there's no ${label} type yet. Choose what it renews as under Renews as once you've made it.`};
}

/** {typeIds, line}: the club's types a parent's membership goes with, or the words for why there are none. */
export function matchChildTypes(keys, clubTypes = [], {allSuggestions = []} = {}) {
  const labels = (keys ?? []).map(k => keyLabel(k, allSuggestions)).filter(Boolean);
  const fits = (clubTypes ?? []).filter(t => !t.archivedAt && !t.needsChildMembership);
  const typeIds = [];
  for (const label of labels) {
    const exact = fits.filter(t => sameName(t, label));
    const found = exact.length ? exact : fits.filter(t => startsWithName(t, label));
    for (const t of found) if (!typeIds.includes(t.id)) typeIds.push(t.id);
  }
  if (typeIds.length) return {typeIds, line: null};
  const named = labels.length ? labels.join(' or ') : "children's";
  return {typeIds: [], line: `It doesn't need a child's membership yet: there's no ${named} type to link to. Turn on Needs a child's membership once you've made one.`};
}

/**
 * A type editor draft from a server suggestion (GET suggestions) or a start-from
 * chip: carries the D42–D45 fields and maps renewsAsKey / childKeys onto this
 * club's own types the way the API's quick add does. `linkLines` says what
 * couldn't be linked, for the editor to show. Fields the editor already takes
 * from a quick draft are left to it.
 */
export function suggestionTypeFields(suggestion, clubTypes = [], allSuggestions = []) {
  const covers = suggestion?.covers ?? COVERS.onePerson;
  const linkLines = [];
  let renewsAsTypeId = null;
  if (suggestion?.renewsAsKey) {
    const found = matchRenewsAs(suggestion.renewsAsKey, clubTypes, {covers, allSuggestions});
    renewsAsTypeId = found.typeId;
    if (found.line) linkLines.push(found.line);
  }
  // R16-L1: an Introductory start takes the ages of the type it renews into (Senior Adult 26 and over), so nobody can
  // join it who couldn't renew into it. Only when that type has ages; otherwise the suggestion's own stand.
  const renewsAsType = renewsAsTypeId ? (clubTypes ?? []).find(t => t.id === renewsAsTypeId) : null;
  const ages = renewsAsType && (renewsAsType.minAge != null || renewsAsType.maxAge != null)
      ? {minAge: renewsAsType.minAge ?? null, maxAge: renewsAsType.maxAge ?? null} : {};
  let needsChildMembership = !!suggestion?.needsChildMembership;
  let childMembershipTypeIds = [];
  if (needsChildMembership) {
    const found = matchChildTypes(suggestion?.childKeys ?? [], clubTypes, {allSuggestions});
    childMembershipTypeIds = found.typeIds;
    if (found.line) { linkLines.push(found.line); needsChildMembership = false; }
  }
  return {
    covers,
    slots: (suggestion?.slots ?? []).map((s, i) => ({id: null, label: s.label, minCount: s.minCount ?? 0, maxCount: s.maxCount ?? 1, minAge: s.minAge ?? null, maxAge: s.maxAge ?? null, nextTypeOnAgeOutId: null, sortOrder: i})),
    conditions: (suggestion?.conditions ?? []).map(text => ({id: null, text})),
    newToClubOnly: !!suggestion?.newToClubOnly,
    newToClubYears: null,
    renewsAsTypeId,
    needsChildMembership,
    childMembershipTypeIds,
    description: suggestion?.description ?? null,
    linkLines,
    ...ages,
  };
}

const ageNum = (v) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));
/** "26 and over" / "under 18" / "18 to 25": lower case, the way X25/X28 word ages inside a sentence. */
const ageSpan = (min, max) => {
  if (min != null && max != null) return min === max ? `${min}` : `${min} to ${max}`;
  if (min != null) return `${min} and over`;
  if (max != null) return `under ${max + 1}`;
  return 'anyone';
};

/**
 * R16-L1: the editor's preview of the API's renewsAsAgeWarning, in the API's words (R17 L2): an Introductory type whose ages
 * let in someone the type it renews into doesn't take. "Senior Adult is for 26 and over, but this type also takes people
 * aged 18 to 25. Give it the same ages, or they'll renew into a type their age doesn't fit." Null when its ages sit inside the other type's (or either is a group, whose ages are on its places).
 */
export function renewsAsAgeWarning(form, renewsAsType) {
  if (!form || !renewsAsType) return null;
  if ((form.covers ?? COVERS.onePerson) === COVERS.group || (renewsAsType.covers ?? COVERS.onePerson) === COVERS.group) return null;
  const a = ageNum(form.minAge), b = ageNum(form.maxAge);
  const c = ageNum(renewsAsType.minAge), d = ageNum(renewsAsType.maxAge);
  const gaps = [];
  if (c != null && (a == null || a < c) && (b == null || b >= (a ?? 0))) {
    const top = b != null && b < c - 1 ? b : c - 1;
    gaps.push(a == null || a <= 0 ? `under ${top + 1}` : ageSpan(a, top));
  }
  if (d != null && (b == null || b > d)) {
    const bottom = a != null && a > d + 1 ? a : d + 1;
    gaps.push(b == null ? `${bottom} and over` : ageSpan(bottom, b));
  }
  if (!gaps.length) return null;
  const name = String(renewsAsType.name ?? 'That type').trim();
  return `${name} is for ${ageSpan(c, d)}, but this type also takes people aged ${gaps.join(' and ')}. Give it the same ages, or they'll renew into a type their age doesn't fit.`;
}

/**
 * The quick form's extra start-from chips (only where "All settings" is
 * offered): they open the full editor, since places, conditions and links
 * can't be set in the quick form. Mirrors the API's family / introSenior /
 * parentPlay suggestions (MembershipSuggestions).
 */
export const START_FROM_FULL = Object.freeze([
  {key: 'family', name: 'Family', description: 'Two adults and their children living at home, on one membership.', displayAs: 'Membership', minAge: null, maxAge: null,
    amountMinor: 57000, length: 'Ongoing', termLength: 1, termUnit: 'Year', requiresApproval: false, covers: COVERS.group,
    slots: [{label: 'Adults', minCount: 1, maxCount: 2, minAge: 26, maxAge: null}, {label: 'Children', minCount: 0, maxCount: 6, minAge: null, maxAge: 25}],
    conditions: ['We live at the same address', 'Children are in full-time education'], newToClubOnly: false, needsChildMembership: false, childKeys: []},
  {key: 'introSenior', name: 'Introductory', description: 'First year only, for adults new to the club. Renews as Senior.', displayAs: 'Membership', minAge: 18, maxAge: null,
    amountMinor: 17500, length: 'Ongoing', termLength: 1, termUnit: 'Year', requiresApproval: false, covers: COVERS.onePerson, slots: [],
    conditions: ["I'm new to the game"], newToClubOnly: true, renewsAsKey: 'senior', needsChildMembership: false, childKeys: []},
  {key: 'parentPlay', name: 'Parent Play', description: "For a parent of a junior member, to play with their child. Ends with the child's membership.", displayAs: 'Membership', minAge: 18, maxAge: null,
    amountMinor: 4500, length: 'Ongoing', termLength: 1, termUnit: 'Year', requiresApproval: false, covers: COVERS.onePerson, slots: [],
    conditions: [], newToClubOnly: false, needsChildMembership: true, childKeys: ['junior']},
]);

/** A suggestion that needs the full editor (the quick form can't set places, conditions, new-to-club or a child). */
export const suggestionNeedsFullEditor = (s) => !!s && (s.covers === COVERS.group || !!s.newToClubOnly || !!s.needsChildMembership || (s.conditions ?? []).length > 0);

/**
 * What a renewal costs and what it renews into (D44, R15 N3). An Introductory
 * type renews as another type at that type's price, so "Then £350 a year" (its
 * own first-year price) was wrong: it is "Then £570 a year as Family". The
 * price comes from the API's renewsAsLine ("Renews as Family at £570 a year");
 * a type that renews as itself keeps its own option summary.
 * Returns {summary, asTypeName}; asTypeName is null when it renews as itself.
 */
export function renewalTerms({type = null, elig = null, option = null} = {}) {
  const own = option?.summary ?? null;
  const asName = elig?.renewsAsTypeName || type?.renewsAsTypeName || null;
  const asId = elig?.renewsAsTypeId ?? type?.renewsAsTypeId ?? null;
  if (!asName || (asId && type?.id && String(asId) === String(type.id))) return {summary: own, asTypeName: null};
  const line = elig?.renewsAsLine || type?.renewsAsLine || '';
  const m = /\bat\s+(.+?)\.?\s*$/.exec(String(line));
  return {summary: m ? m[1] : own, asTypeName: asName};
}

/** "Then £570 a year as Family, renewed automatically." (or the type's own price when it renews as itself). */
export function renewalThenLine(terms) {
  if (!terms?.summary) return 'Renewed automatically.';
  return terms.asTypeName
    ? `Then ${terms.summary} as ${terms.asTypeName}, renewed automatically.`
    : `Then ${terms.summary}, renewed automatically.`;
}

/** The saved-card agreement (D35), naming what the card is charged for on renewal. */
export function savedCardAgreement({clubName, typeName, noun, terms, amountText}) {
  const club = clubName ?? 'the club';
  const amount = terms?.summary || amountText;
  if (terms?.asTypeName) {
    return `I agree that ${club}, through Tap Leagues, may charge this card ${amount} for my ${withNoun(terms.asTypeName, noun)}, which my ${withNoun(typeName, noun)} renews as, until it is cancelled. I can stop future payments at any time before a renewal from my memberships page.`;
  }
  return `I agree that ${club}, through Tap Leagues, may charge this card ${amount} for my ${withNoun(typeName, noun)} until it is cancelled. I can stop future payments at any time before a renewal from my memberships page.`;
}
